/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useCallback, useEffect, useRef } from 'react';

// Cursor annotation trail, per the Live Session Annotations UI spec (section 3):
// each point lives 500ms; segments thin from 5px to 1px and fade from 0.9 to 0 opacity.
const TRAIL_MS = 500;

const paintTrail = (ctx, points, now) => {
  ctx.lineCap = 'round';
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    if (!curr.gap) {
      const life = Math.max(0, 1 - (now - curr.t) / TRAIL_MS);
      ctx.strokeStyle = `rgba(245, 158, 11, ${0.9 * life})`;
      ctx.lineWidth = 1 + 4 * life;
      ctx.beginPath();
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(curr.x, curr.y);
      ctx.stroke();
    }
  }
};

/**
 * Draws a fading pointer trail on `canvasRef`, sized to match its own box (DPR-aware).
 * `addPoint(x, y)` takes CSS px relative to the canvas; `breakTrail()` stops the next
 * point joining the previous one (pointer left the frame, annotate turned off, ...).
 * The animation loop only runs while there are points to fade.
 * `active` should flip whenever the canvas mounts/unmounts (e.g. the call modal opening).
 */
const useAnnotationTrail = (canvasRef, active) => {
  const pointsRef = useRef([]);
  const gapRef = useRef(true);
  const frameRef = useRef(null);

  // Keep the backing store matched to the rendered size so the line stays crisp.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!active || !canvas || typeof ResizeObserver === 'undefined') { return undefined; }
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(canvas.clientWidth * dpr);
      canvas.height = Math.round(canvas.clientHeight * dpr);
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    return () => {
      observer.disconnect();
      pointsRef.current = [];
      gapRef.current = true;
    };
  }, [canvasRef, active]);

  const tick = useCallback(() => {
    frameRef.current = null;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) {
      pointsRef.current = [];
      return;
    }
    const now = performance.now();
    pointsRef.current = pointsRef.current.filter((p) => now - p.t < TRAIL_MS);

    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    paintTrail(ctx, pointsRef.current, now);

    if (pointsRef.current.length) {
      frameRef.current = requestAnimationFrame(tick);
    }
  }, [canvasRef]);

  const addPoint = useCallback((x, y) => {
    pointsRef.current.push({ x, y, t: performance.now(), gap: gapRef.current });
    gapRef.current = false;
    if (frameRef.current === null) {
      frameRef.current = requestAnimationFrame(tick);
    }
  }, [tick]);

  // Existing segments are left to finish fading -- only the next join is suppressed.
  const breakTrail = useCallback(() => {
    gapRef.current = true;
  }, []);

  useEffect(() => () => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  return { addPoint, breakTrail };
};

export default useAnnotationTrail;
